import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_BUSINESS_METRICS, GET_SOFTWARE_EXPENSES } from "../../../../utils/GraphQL/queries";
import { REFRESH_SOFTWARE_EXPENSES } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import "./businessMetrics.css";

/**
 * Business metrics. ONE component, mounted twice: as the "Business" tab on the
 * Metrics page and as the "Metrics" tab on Business Ledger. It owns its own
 * year state and query, so it renders correctly inside any host page and shares
 * no state with the ledger table.
 *
 * Styling composes the global bdr-* utilities from index.css rather than
 * introducing a token layer — the token system is mobile-only.
 *
 * Charts are hand-rolled inline SVG: no charting library is installed, every
 * chart here is bars/lines over <= 14 points, and CI runs warnings-as-errors so
 * a lib with lint noise would fail the build.
 *
 * COLOR (validated with the dataviz validator against the dark panel surface):
 * - Ranked tables use single-hue proportion bars — magnitude, not identity —
 *   which sidesteps the categorical palette entirely.
 * - Job-type mix is the only categorical use: M #2196F3 / R #4CAF50 /
 *   Other #9E9E9E passes CVD, normal-vision and contrast, and always ships with
 *   a legend + values so identity is never carried by color alone.
 * - Where the cal* categorical set IS needed, the validated order is
 *   peacock -> tangerine -> grape -> banana -> flamingo. The app's raw order
 *   put the two red-oranges adjacent (normal-vision dE 9.4, a hard fail);
 *   reordering lifts it to 19.8.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const JOB_TYPE_COLORS = { m: "#2196F3", r: "#4CAF50", other: "#9E9E9E" };
const REVENUE_COLOR = "#05F842";

const n = (v) => Number(v || 0);

/** Compact money for axis ticks and dense chart labels ($386k, $1.2M). */
function compactMoney(centsStr) {
  const d = n(centsStr) / 100;
  if (Math.abs(d) >= 1000000) return `$${(d / 1000000).toFixed(1)}M`;
  if (Math.abs(d) >= 1000) return `$${Math.round(d / 1000)}k`;
  return `$${Math.round(d)}`;
}

/**
 * Delta percent from the server's integer pairs. This is the ONE permitted
 * client-side computation — it is not money, and identical integers give
 * identical results on web and mobile. One decimal place on both platforms.
 */
function deltaPct(currentCents, priorCents) {
  const prior = n(priorCents);
  if (!prior) return null;
  return ((n(currentCents) - prior) / prior) * 100;
}

const DeltaBadge = ({ value, suffix = "%" }) => {
  if (value === null || value === undefined || !isFinite(value)) {
    return <span className="bm-delta bm-delta-none">--</span>;
  }
  const up = value >= 0;
  return (
    <span className={`bm-delta ${up ? "bm-delta-up" : "bm-delta-down"}`}>
      {up ? "▲" : "▼"} {Math.abs(value).toFixed(1)}
      {suffix}
    </span>
  );
};

const StatTile = ({ label, value, delta, note, projected }) => (
  <div className={`bm-tile bdr-grad-dark-card bdr-shadow-med ${projected ? "bm-tile-projected" : ""}`}>
    <span className="bm-tile-label">{label}</span>
    <span className="bm-tile-value">{value}</span>
    {projected && <span className="bm-tile-badge">PROJECTED</span>}
    {delta !== undefined && <DeltaBadge value={delta} />}
    {note && <span className="bm-tile-note">{note}</span>}
  </div>
);

/* ── Software Expenses (2026-09-04, plan REV 2 §3) ─────────────────────────
   Vendor spend for the current month from the persisted server doc. Own
   query, independent of the year-scoped metrics query. Null cents = that
   vendor never fetched successfully → "--", NEVER $0 (false-zero rule).
   Refresh triggers the real vendor pull; the server throttles via the doc's
   updatedAtMs (<10min → stored doc returned untouched), so an unchanged
   updatedAtMs after the mutation means "already fresh", not a failure. */
const SW_VENDOR_LABELS = {
  aws: "AWS",
  atlas: "MongoDB Atlas",
  twilio: "Twilio",
  sendgrid: "SendGrid",
  claude: "Claude",
  // Inventory hardening 2026-09-18 (D27): receipt OCR + door classification.
  openai: "OpenAI",
};

const SoftwareExpensesPanel = () => {
  const { handleSnack } = useSnackbar();
  const { data, refetch } = useQuery(GET_SOFTWARE_EXPENSES, {
    fetchPolicy: "network-only",
  });
  const [refreshExpenses, { loading: refreshing }] = useMutation(REFRESH_SOFTWARE_EXPENSES);
  const report = data?.getSoftwareExpenses;

  const money = (c) => (c == null ? "--" : priceStrFormatter(c));
  const projNote = (v) => {
    if (v?.error === "not-configured") return "awaiting setup";
    if (v?.error) return "errored — showing last good";
    if (v?.projectedCents == null) return null;
    return `proj. ${priceStrFormatter(v.projectedCents)}${v.basis === "run-rate" ? " (run-rate)" : ""}`;
  };

  const onRefresh = async () => {
    try {
      const before = report?.updatedAtMs;
      const res = await refreshExpenses();
      const after = res?.data?.refreshSoftwareExpenses?.updatedAtMs;
      await refetch();
      if (after && before && after === before) {
        const mins = Math.max(1, Math.round((Date.now() - Number(after)) / 60000));
        handleSnack(`Already fresh — pulled ${mins}m ago`, 3500);
      } else {
        handleSnack("Software expenses refreshed", 3000);
      }
    } catch (err) {
      handleSnack(`Refresh failed: ${err.message || err}`, 5000);
    }
  };

  const byVendor = {};
  (report?.vendors || []).forEach((v) => {
    byVendor[v.vendor] = v;
  });

  return (
    <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <div className="bm-swx-header">
        <h3 className="bm-panel-title">Software Expenses{report ? ` — ${report.month}` : ""}</h3>
        <button
          type="button"
          className="bm-retry bdr-grad-admin-btn"
          disabled={refreshing}
          onClick={onRefresh}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <div className="bm-tiles">
        {Object.entries(SW_VENDOR_LABELS).map(([key, label]) => {
          const v = byVendor[key];
          const notes = [projNote(v)];
          if (key === "twilio" && report?.twilioTrackedBaseCents != null) {
            notes.push(`tracked base ${money(report.twilioTrackedBaseCents)}`);
          }
          return (
            <StatTile
              key={key}
              label={label}
              value={money(v?.mtdCents)}
              note={notes.filter(Boolean).join(" · ") || null}
            />
          );
        })}
        <StatTile
          projected
          label="Projected Month Total"
          value={report ? money(report.totalProjectedCents) : "--"}
          note={report ? `MTD so far ${money(report.totalMtdCents)}` : "no data yet — hit Refresh"}
        />
      </div>
    </div>
  );
};

/* ── Annual revenue: bars, current year split actual + projected remainder ── */
const AnnualRevenueChart = ({ byYear, projection, currentYear }) => {
  const W = 900;
  const H = 260;
  const padL = 54;
  const padB = 30;
  const padT = 16;

  const bars = byYear.map((y) => {
    const actual = n(y.revenueCents);
    const projected =
      y.year === currentYear && projection ? n(projection.projectedRemainderCents) : 0;
    return { year: y.year, actual, projected, total: actual + projected };
  });

  const max = Math.max(...bars.map((b) => b.total), 1);
  const plotW = W - padL - 8;
  const plotH = H - padB - padT;
  const slot = plotW / bars.length;
  const barW = Math.min(46, slot * 0.62);

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bm-svg" role="img" aria-label="Annual revenue by year">
      <defs>
        {/* Projected remainder is hatched, never a solid fill — a forecast must
            not read like settled revenue. */}
        <pattern id="bm-hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill="rgba(5,248,66,0.14)" />
          <line x1="0" y1="0" x2="0" y2="6" stroke={REVENUE_COLOR} strokeWidth="2" opacity="0.55" />
        </pattern>
      </defs>

      {ticks.map((t, i) => {
        const y = padT + plotH - (t / max) * plotH;
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - 8} y2={y} className="bm-grid" />
            <text x={padL - 8} y={y + 4} textAnchor="end" className="bm-axis-text">
              {compactMoney(String(t))}
            </text>
          </g>
        );
      })}

      {bars.map((b, i) => {
        const x = padL + slot * i + (slot - barW) / 2;
        const actualH = (b.actual / max) * plotH;
        const projH = (b.projected / max) * plotH;
        const actualY = padT + plotH - actualH;
        const projY = actualY - projH;
        return (
          <g key={b.year}>
            {projH > 0 && (
              <rect
                x={x}
                // 2px surface gap between the actual and projected segments so
                // the two fills never touch.
                y={projY}
                width={barW}
                height={Math.max(0, projH - 2)}
                rx="4"
                fill="url(#bm-hatch)"
                stroke={REVENUE_COLOR}
                strokeDasharray="3 2"
                strokeWidth="1"
                opacity="0.9"
              />
            )}
            <rect x={x} y={actualY} width={barW} height={actualH} rx="4" fill={REVENUE_COLOR} opacity="0.85" />
            <title>
              {b.year}: {priceStrFormatter(String(b.actual))}
              {b.projected ? ` actual + ${priceStrFormatter(String(b.projected))} projected` : ""}
            </title>
            <text x={x + barW / 2} y={H - padB + 16} textAnchor="middle" className="bm-axis-text">
              {b.year}
            </text>
            {/* Selective direct labels — endpoints and the current year only,
                never a number on every bar. */}
            {(i === 0 || i === bars.length - 1 || b.year === currentYear) && (
              <text x={x + barW / 2} y={(projH > 0 ? projY : actualY) - 5} textAnchor="middle" className="bm-bar-label">
                {compactMoney(String(b.total))}
              </text>
            )}
          </g>
        );
      })}
      <line x1={padL} y1={padT + plotH} x2={W - 8} y2={padT + plotH} className="bm-axis-line" />
    </svg>
  );
};

/**
 * Rolling 12-month revenue. Each point is the sum of that month and the 11
 * before it, which strips seasonality out entirely — it is the single best
 * "are we actually growing" view, because a rising line cannot be a good
 * October and a falling one cannot be a slow January.
 */
const RollingRevenueChart = ({ monthlySeries }) => {
  const W = 900;
  const H = 220;
  const padL = 54;
  const padB = 26;
  const padT = 14;

  const points = useMemo(() => {
    if (!monthlySeries || monthlySeries.length < 12) return [];
    const sorted = [...monthlySeries].sort((a, b) => a.year - b.year || a.month - b.month);
    const out = [];
    for (let i = 11; i < sorted.length; i++) {
      let sum = 0;
      for (let k = 0; k < 12; k++) sum += n(sorted[i - k].revenueCents);
      out.push({ year: sorted[i].year, month: sorted[i].month, value: sum });
    }
    return out;
  }, [monthlySeries]);

  if (points.length < 2) {
    return <div className="bm-empty-small">Not enough history for a rolling 12-month view.</div>;
  }

  const max = Math.max(...points.map((p) => p.value), 1);
  const plotW = W - padL - 8;
  const plotH = H - padB - padT;
  const x = (i) => padL + (i / (points.length - 1)) * plotW;
  const y = (v) => padT + plotH - (v / max) * plotH;
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.value)}`).join(" ");
  const last = points[points.length - 1];
  const first = points[0];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bm-svg" role="img" aria-label="Rolling 12-month revenue">
      {[0, 0.5, 1].map((f) => {
        const yy = padT + plotH - f * plotH;
        return (
          <g key={f}>
            <line x1={padL} y1={yy} x2={W - 8} y2={yy} className="bm-grid" />
            <text x={padL - 8} y={yy + 4} textAnchor="end" className="bm-axis-text">
              {compactMoney(String(Math.round(max * f)))}
            </text>
          </g>
        );
      })}
      <path d={d} fill="none" stroke={REVENUE_COLOR} strokeWidth="2" />
      {/* Endpoints only — never a marker on every point. */}
      <circle cx={x(points.length - 1)} cy={y(last.value)} r="4" fill={REVENUE_COLOR} />
      <text x={x(points.length - 1) - 6} y={y(last.value) - 9} textAnchor="end" className="bm-bar-label">
        {compactMoney(String(last.value))}
      </text>
      <text x={padL} y={H - 6} className="bm-axis-text">
        {first.year}
      </text>
      <text x={W - 8} y={H - 6} textAnchor="end" className="bm-axis-text">
        {last.year}
      </text>
      <line x1={padL} y1={padT + plotH} x2={W - 8} y2={padT + plotH} className="bm-axis-line" />
    </svg>
  );
};

/**
 * Seasonality, indexed against an average month (1.0 baseline).
 *
 * The baseline index is a LONG-RUN signal by construction — it is the mean
 * month-share across every complete year, so it cannot be recomputed for a
 * single year and still mean "seasonality". One year's monthly pattern is just
 * that year's revenue, which the Month Detail table already shows.
 *
 * So when a specific year is selected we don't replace the index — we overlay
 * that year against it, which answers the question actually worth asking:
 * "did this year follow our normal shape, or was it strange?"
 */
const SeasonalityChart = ({ seasonality, sampleYears, yearIndex, yearLabel }) => {
  const W = 900;
  const H = 210;
  const padL = 44;
  const padB = 26;
  const padT = 14;
  const plotW = W - padL - 8;
  const plotH = H - padB - padT;
  const slot = plotW / 12;
  const comparing = !!yearIndex;
  const barW = Math.min(comparing ? 20 : 42, slot * (comparing ? 0.34 : 0.6));
  const max = Math.max(
    ...seasonality.map((s) => s.index),
    ...(yearIndex || []),
    1.2
  );

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bm-svg" role="img" aria-label="Revenue by calendar month, indexed to an average month">
      {[0.5, 1, 1.5, 2].map((t) => {
        const y = padT + plotH - (t / max) * plotH;
        if (y < padT) return null;
        return (
          <g key={t}>
            <line x1={padL} y1={y} x2={W - 8} y2={y} className={t === 1 ? "bm-grid-strong" : "bm-grid"} />
            <text x={padL - 8} y={y + 4} textAnchor="end" className="bm-axis-text">
              {t.toFixed(1)}x
            </text>
          </g>
        );
      })}
      {seasonality.map((s, i) => {
        const h = (s.index / max) * plotH;
        // Paired bars when comparing: typical on the left, this year on the right.
        const groupX = padL + slot * i;
        const x = comparing
          ? groupX + slot / 2 - barW - 1
          : groupX + (slot - barW) / 2;
        const y = padT + plotH - h;
        const hot = s.index >= 1;
        const yv = yearIndex ? yearIndex[i] : null;
        return (
          <g key={s.month}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx="4"
              fill={comparing ? "#9E9E9E" : hot ? "#039BE5" : "#8E24AA"}
              opacity={comparing ? 0.55 : 0.85}
            />
            <title>
              {MONTHS[i]}: typical {s.index.toFixed(2)}x
              {yv != null ? `, ${yearLabel} ${yv.toFixed(2)}x` : ""}
            </title>
            {yv != null && (
              <>
                {/* 2px surface gap so the paired fills never touch. */}
                <rect
                  x={groupX + slot / 2 + 1}
                  y={padT + plotH - (yv / max) * plotH}
                  width={barW}
                  height={(yv / max) * plotH}
                  rx="4"
                  fill={yv >= s.index ? "#039BE5" : "#8E24AA"}
                  opacity="0.9"
                />
                <text
                  x={groupX + slot / 2 + 1 + barW / 2}
                  y={padT + plotH - (yv / max) * plotH - 4}
                  textAnchor="middle"
                  className="bm-bar-label"
                  style={{ fontSize: 9 }}
                >
                  {yv.toFixed(2)}
                </text>
              </>
            )}
            {!comparing && (
              <text x={x + barW / 2} y={y - 5} textAnchor="middle" className="bm-bar-label">
                {s.index.toFixed(2)}
              </text>
            )}
            <text x={groupX + slot / 2} y={H - padB + 15} textAnchor="middle" className="bm-axis-text">
              {MONTHS[i]}
            </text>
          </g>
        );
      })}
      <line x1={padL} y1={padT + plotH} x2={W - 8} y2={padT + plotH} className="bm-axis-line" />
      <text x={padL} y={H - 4} className="bm-footnote-text">
        1.0 = an average month · typical shape from {sampleYears} complete years
        {comparing ? ` · grey = typical, colour = ${yearLabel}` : ""}
      </text>
    </svg>
  );
};

/* ── Job-type mix over time: stacked shares, M vs R vs Other ── */
const JobTypeMix = ({ byYear, selectedYear }) => {
  const rows = byYear.filter((y) => y.jobs > 0);
  return (
    <div className="bm-mix">
      <div className="bm-mix-head">
        <span className="bm-mix-year" />
        <div className="bm-legend">
          {[
            ["m", "Maintenance"],
            ["r", "Refinish"],
            ["other", "Other"],
          ].map(([k, label]) => (
            <span key={k} className="bm-legend-item">
              <span className="bm-legend-dot" style={{ background: JOB_TYPE_COLORS[k] }} />
              {label}
            </span>
          ))}
        </div>
        <span className="bm-mix-pct bm-mix-pct-head">M / R</span>
      </div>
      {rows.map((y) => {
        const total = y.mJobs + y.rJobs + y.otherJobs || 1;
        const seg = [
          { k: "m", v: y.mJobs },
          { k: "r", v: y.rJobs },
          { k: "other", v: y.otherJobs },
        ];
        // The bars always span the whole history — that trend IS the point of
        // the chart. Highlighting the selected year keeps it consistent with the
        // averages below, which ARE scoped to the selection.
        const isSelected = selectedYear != null && y.year === selectedYear;
        return (
          <div key={y.year} className={`bm-mix-row ${isSelected ? "bm-mix-row-selected" : ""}`}>
            <span className="bm-mix-year">{y.year}</span>
            <div className="bm-mix-bar">
              {seg.map((s) =>
                s.v > 0 ? (
                  <span
                    key={s.k}
                    className="bm-mix-seg"
                    style={{ width: `${(s.v / total) * 100}%`, background: JOB_TYPE_COLORS[s.k] }}
                    title={`${y.year} ${s.k.toUpperCase()}: ${s.v} jobs (${Math.round((s.v / total) * 100)}%)`}
                  />
                ) : null
              )}
            </div>
            <span className="bm-mix-pct">
              {Math.round((y.mJobs / total) * 100)}% / {Math.round((y.rJobs / total) * 100)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Ranked table with an inline proportion bar. Single hue: the bar encodes
 * MAGNITUDE, not identity, so this needs no categorical palette at all.
 */
const RankedTable = ({ title, rows, note }) => {
  const max = Math.max(...rows.map((r) => n(r.revenueCents)), 1);
  return (
    <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <h4 className="bm-panel-title">{title}</h4>
      {rows.length === 0 ? (
        <div className="bm-empty-small">No data for this period.</div>
      ) : (
        <table className="bm-table">
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.label}-${i}`}>
                <td className="bm-rank-label" title={r.label}>
                  {r.label}
                </td>
                <td className="bm-rank-bar-cell">
                  <span
                    className="bm-rank-bar"
                    style={{ width: `${Math.max(2, (n(r.revenueCents) / max) * 100)}%` }}
                  />
                </td>
                <td className="bm-rank-jobs">{r.jobs}</td>
                <td className="bm-rank-value">{priceStrFormatter(r.revenueCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {note && <div className="bm-panel-note">{note}</div>}
    </div>
  );
};

const YearPills = ({ years, selected, onSelect, allYears }) => (
  <div className="bm-year-pills">
    <button
      className={`bm-pill ${allYears ? "bm-pill-active" : ""}`}
      onClick={() => onSelect(null)}
      type="button"
    >
      All Years
    </button>
    {years.map((y) => (
      <button
        key={y}
        className={`bm-pill ${!allYears && y === selected ? "bm-pill-active" : ""}`}
        onClick={() => onSelect(y)}
        type="button"
      >
        {y}
      </button>
    ))}
  </div>
);

const BusinessMetricsSection = () => {
  // null = All Years
  const [selectedYear, setSelectedYear] = useState(null);
  const allYears = selectedYear === null;

  const { data, loading, error, refetch } = useQuery(GET_BUSINESS_METRICS, {
    variables: { year: selectedYear, allYears },
    // network-only, matching BusinessLedgerPage. Apollo's DEFAULT is
    // cache-first, which would serve session-start data forever and defeat the
    // point of the server's short TTL.
    fetchPolicy: "network-only",
  });

  const m = data?.getBusinessMetrics;

  const years = useMemo(() => {
    if (!m?.byYear) return [];
    return m.byYear.map((y) => y.year).sort((a, b) => b - a);
  }, [m]);

  if (loading && !m) {
    return (
      <div className="bm-root">
        <div className="bm-skeleton-row">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="bm-skeleton-tile" />
          ))}
        </div>
        <div className="bm-skeleton-chart" />
        <div className="bm-loading">Loading business metrics…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bm-root">
        <div className="bm-error bdr-border-thin">
          <p className="bm-error-title">Couldn't load business metrics.</p>
          <p className="bm-error-detail">{error.message}</p>
          <button type="button" className="bm-retry bdr-grad-admin-btn" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!m) return <div className="bm-root"><div className="bm-loading">No metrics available.</div></div>;

  const currentYear = m.meta?.displayYear;
  const c = m.comparisons || {};
  const proj = m.projection;
  // Only the in-progress year has a remainder to project. All Years shows it
  // (the trend chart's hatched bar is the current year), a historical year does
  // not.
  const showProjection = allYears || selectedYear === currentYear;

  const ttmDelta = deltaPct(c.ttmVsPriorTtm?.currentCents, c.ttmVsPriorTtm?.priorCents);
  const ytdDelta = deltaPct(c.ytdVsSamePeriodLastYear?.currentCents, c.ytdVsSamePeriodLastYear?.priorCents);
  const ytdJobsDelta =
    c.ytdVsSamePeriodLastYear?.priorJobs
      ? ((c.ytdVsSamePeriodLastYear.currentJobs - c.ytdVsSamePeriodLastYear.priorJobs) /
          c.ytdVsSamePeriodLastYear.priorJobs) *
        100
      : null;
  const avgDelta = deltaPct(
    c.ttmVsPriorTtm?.currentAvgJobValueCents,
    c.ttmVsPriorTtm?.priorAvgJobValueCents
  );

  // The selected year's own shape, on the SAME mean-1 scale as the long-run
  // index (share of that year's total x 12), so the two are directly
  // comparable. A ratio of integers, not a money value — the same category as
  // the delta percent, and identical on both platforms.
  //
  // The in-progress year is excluded: its later months are empty, so it would
  // read as a catastrophic second half rather than a year still happening.
  const selectedYearIndex = (() => {
    if (allYears || !m.byMonth?.length) return null;
    if (selectedYear === currentYear) return null;
    const total = m.byMonth.reduce((s, mo) => s + n(mo.revenueCents), 0);
    if (!total) return null;
    return m.byMonth.map((mo) => (n(mo.revenueCents) / total) * 12);
  })();

  // Strongest / weakest calendar month, straight off the seasonal index.
  const seasonalExtremes = (() => {
    if (!m.seasonality?.length) return null;
    const withNames = m.seasonality.map((s) => ({ ...s, name: MONTHS[s.month - 1] }));
    const sorted = [...withNames].sort((a, b) => b.index - a.index);
    return { best: sorted[0], worst: sorted[sorted.length - 1] };
  })();

  // Zips appearing for the first time in each year — a service-area expansion
  // signal. Starts at 2015: 2013 has only 71% zip coverage across 48 jobs, so
  // 2014 would otherwise report a flood of fake "new" zips that are simply the
  // first properly-recorded ones.
  const newZipsByYear = (() => {
    if (!m.zips?.length) return [];
    const counts = {};
    for (const z of m.zips) {
      if (!z.firstYear || z.firstYear < 2015) continue;
      counts[z.firstYear] = (counts[z.firstYear] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([year, count]) => ({ year: Number(year), count }))
      .sort((a, b) => b.year - a.year)
      .slice(0, 6);
  })();

  // Referral attribution coverage over time. Falling coverage reads as data rot
  // until you notice it tracks the maintenance share — revisits inherit the
  // original job's source rather than having a new one.
  const coverage = (m.byYear || [])
    .filter((y) => y.year >= (m.meta?.firstFullYear ?? 2014) && y.jobs > 0)
    .map((y) => ({ year: y.year, pct: Math.round((y.attributedJobs / y.jobs) * 100) }));

  // Refinish vs maintenance economics — the mix has shifted hard toward
  // maintenance, so the average job value by type is the number that explains
  // why total revenue and job count can move in different directions.
  const typeEconomics = (() => {
    const rows = allYears
      ? m.byYear
      : (m.byYear || []).filter((y) => y.year === selectedYear);
    if (!rows?.length) return null;
    const mCents = rows.reduce((s, y) => s + n(y.mRevenueCents), 0);
    const mJobs = rows.reduce((s, y) => s + (y.mJobs || 0), 0);
    const rCents = rows.reduce((s, y) => s + n(y.rRevenueCents), 0);
    const rJobs = rows.reduce((s, y) => s + (y.rJobs || 0), 0);
    const mAvg = mJobs ? Math.round(mCents / mJobs) : 0;
    const rAvg = rJobs ? Math.round(rCents / rJobs) : 0;
    return {
      mAvg: String(mAvg),
      rAvg: String(rAvg),
      ratio: mAvg ? rAvg / mAvg : null,
    };
  })();

  const comparisonRows = [
    ["This month vs last month", c.monthOverMonth],
    ["This month vs same month last year", c.monthVsSameMonthLastYear],
    ["This quarter vs last quarter", c.quarterOverQuarter],
    ["This quarter vs same quarter last year", c.quarterVsSameQuarterLastYear],
    ["Year to date vs same period last year", c.ytdVsSamePeriodLastYear],
    ["Trailing 12 months vs prior 12", c.ttmVsPriorTtm],
  ].filter(([, v]) => !!v);

  return (
    <div className="bm-root bdr-dark-placeholder">
      <YearPills years={years} selected={selectedYear} onSelect={setSelectedYear} allYears={allYears} />

      {/* ── KPI tiles ── */}
      <div className="bm-tiles">
        <StatTile
          label="Trailing 12 Months"
          value={priceStrFormatter(c.ttmVsPriorTtm?.currentCents || "0")}
          delta={ttmDelta}
          note="vs prior 12 months"
        />
        <StatTile
          label="Year to Date"
          value={priceStrFormatter(c.ytdVsSamePeriodLastYear?.currentCents || "0")}
          delta={ytdDelta}
          note="vs same period last year"
        />
        <StatTile
          label="Jobs YTD"
          value={String(c.ytdVsSamePeriodLastYear?.currentJobs ?? 0)}
          delta={ytdJobsDelta}
          note={`was ${c.ytdVsSamePeriodLastYear?.priorJobs ?? 0} this time last year`}
        />
        <StatTile
          label="Avg Job Value"
          value={priceStrFormatter(c.ttmVsPriorTtm?.currentAvgJobValueCents || "0")}
          delta={avgDelta}
          note="trailing 12 months"
        />
        {/* A projection only means something for the year still in progress.
            The server always returns the CURRENT year's projection regardless of
            scope, so without this gate, selecting 2019 showed a "Projected Year
            End" tile holding 2026's number — read as a 2019 forecast. */}
        {proj && showProjection && (
          <StatTile
            projected
            label="Projected Year End"
            value={priceStrFormatter(proj.projectedYearEndCents)}
            note={proj.lowConfidence ? `${proj.basisLabel} — low confidence this early in the year` : proj.basisLabel}
          />
        )}
      </div>

      {/* ── Annual trend ── */}
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
        <h4 className="bm-panel-title">Annual Revenue</h4>
        <AnnualRevenueChart byYear={m.byYear} projection={proj} currentYear={currentYear} />
        <div className="bm-panel-note">
          Solid = recorded revenue. Hatched = projected remainder for {currentYear} only — {proj?.basisLabel}.
          {m.cagr && (
            <>
              {" "}
              <strong className="bm-accent">
                {m.cagr.firstYear}–{m.cagr.lastCompleteYear} CAGR {m.cagr.value}%
              </strong>
              .
            </>
          )}{" "}
          {m.meta?.firstFullYear && (
            <>
              {m.meta.firstFullYear - 1} was a partial first year of record-keeping, so it anchors no
              growth comparison.
            </>
          )}
        </div>
      </div>

      {/* ── Rolling 12-month ── */}
      {m.monthlySeries && (
        <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
          <h4 className="bm-panel-title">Rolling 12-Month Revenue</h4>
          <RollingRevenueChart monthlySeries={m.monthlySeries} />
          <div className="bm-panel-note">
            Every point is that month plus the eleven before it, so seasonality cancels out
            entirely — a rising line can't be a good October and a falling one can't be a slow
            January. Select "All Years" to see the full history.
          </div>
        </div>
      )}

      {/* ── Seasonality ── */}
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
        <h4 className="bm-panel-title">
          Seasonality{!allYears && ` — ${selectedYear} vs typical`}
        </h4>
        <SeasonalityChart
          seasonality={m.seasonality}
          sampleYears={m.meta?.sampleYears}
          yearIndex={selectedYearIndex}
          yearLabel={selectedYear}
        />
        <div className="bm-panel-note">
          {seasonalExtremes && (
            <>
              Typically the strongest month is{" "}
              <strong className="bm-accent">{seasonalExtremes.best.name}</strong> at{" "}
              {seasonalExtremes.best.index.toFixed(2)}× an average month; the weakest is{" "}
              <strong>{seasonalExtremes.worst.name}</strong> at{" "}
              {seasonalExtremes.worst.index.toFixed(2)}×.{" "}
            </>
          )}
          {selectedYearIndex ? (
            <>
              The <strong>typical</strong> shape is the long-run average and does not change with
              the year you pick — it needs many complete years to mean anything. Selecting a year
              overlays it so you can see where {selectedYear} ran hotter or colder than normal.
            </>
          ) : (
            <>Select a year to overlay it against this typical shape.</>
          )}
        </div>
      </div>

      {/* ── Period comparisons ── */}
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
        <h4 className="bm-panel-title">Period Comparisons</h4>
        <div className="bm-table-scroll">
          <table className="bm-table bm-table-wide">
            <thead>
              <tr>
                <th>Period</th>
                <th className="bm-num">Current</th>
                <th className="bm-num">Prior</th>
                <th className="bm-num">Change</th>
                <th className="bm-num">Jobs</th>
                <th className="bm-num">Avg Job</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map(([label, v]) => (
                <tr key={label}>
                  <td>
                    {label}
                    {v.spanDays ? <span className="bm-span-note"> · first {v.spanDays} days</span> : null}
                    {v.clamped ? <span className="bm-span-note"> · clamped</span> : null}
                  </td>
                  <td className="bm-num bm-money">{priceStrFormatter(v.currentCents)}</td>
                  <td className="bm-num bm-muted">{priceStrFormatter(v.priorCents)}</td>
                  <td className="bm-num">
                    <DeltaBadge value={deltaPct(v.currentCents, v.priorCents)} />
                  </td>
                  <td className="bm-num">
                    {v.currentJobs} <span className="bm-muted">/ {v.priorJobs}</span>
                  </td>
                  <td className="bm-num">{priceStrFormatter(v.currentAvgJobValueCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bm-panel-note">
          Partial periods are compared like-for-like — a month-to-date is measured against the same
          number of days in the comparison period, not against a whole month.
        </div>
      </div>

      {/* ── Job type mix ── */}
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
        <h4 className="bm-panel-title">Job Type Mix</h4>
        <JobTypeMix byYear={m.byYear} selectedYear={allYears ? null : selectedYear} />
        {typeEconomics && (
          <div className="bm-type-econ">
            <div>
              <span className="bm-tile-label">Avg maintenance job</span>
              <span className="bm-client-value" style={{ color: JOB_TYPE_COLORS.m }}>
                {priceStrFormatter(typeEconomics.mAvg)}
              </span>
            </div>
            <div>
              <span className="bm-tile-label">Avg refinish job</span>
              <span className="bm-client-value" style={{ color: JOB_TYPE_COLORS.r }}>
                {priceStrFormatter(typeEconomics.rAvg)}
              </span>
            </div>
            <div>
              <span className="bm-tile-label">Refinish premium</span>
              <span className="bm-client-value">
                {typeEconomics.ratio === null ? "--" : `${typeEconomics.ratio.toFixed(1)}×`}
              </span>
            </div>
          </div>
        )}
        <div className="bm-panel-note">
          Percentages are maintenance / refinish. RM, HC and Other are a rounding error across the
          whole history and are grouped as "Other". The averages are for{" "}
          {allYears ? "the whole history" : selectedYear} — worth watching, because the mix has
          shifted heavily toward maintenance and the two job types don't earn the same.
        </div>
      </div>

      {/* ── Ranked lists ── */}
      <div className="bm-grid-3">
        <RankedTable
          title={allYears ? "Crew by Revenue (all years)" : `Crew by Revenue (${selectedYear})`}
          rows={(m.crew || []).slice(0, 10).map((r) => ({
            label: r.name,
            jobs: r.jobs,
            revenueCents: r.revenueCents,
          }))}
          note="Revenue is split across the crew on each job, never credited in full to each member. Crew data starts in 2014."
        />
        <RankedTable
          title="Lead Sources"
          rows={(m.leadSources || []).slice(0, 10).map((r) => ({
            label: r.label,
            jobs: r.jobs,
            revenueCents: r.revenueCents,
          }))}
          note={
            `Attributed jobs only. ${m.unattributedCount} jobs have no recorded source — mostly ` +
            `maintenance revisits, which inherit the original job's source rather than having a ` +
            `new one.` +
            (coverage.length
              ? ` Coverage by year — ${coverage
                  .slice(-5)
                  .map((c) => `${c.year}: ${c.pct}%`)
                  .join(" · ")}. The decline is the maintenance side of the business growing, not ` +
                `record-keeping getting worse.`
              : "")
          }
        />
        <RankedTable
          title="Top ZIP Codes"
          rows={(m.zips || []).slice(0, 10).map((r) => ({
            label: r.zip,
            jobs: r.jobs,
            revenueCents: r.revenueCents,
          }))}
          note={
            newZipsByYear.length
              ? `New ZIPs entered per year — ${newZipsByYear
                  .map((z) => `${z.year}: ${z.count}`)
                  .join(" · ")}. Counted from 2015 on: 2013 has only 71% ZIP coverage, so earlier "new" ZIPs would mostly be first-recorded rather than genuinely new.`
              : undefined
          }
        />
      </div>

      {/* ── Clients ── */}
      {m.clients && (
        <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
          <h4 className="bm-panel-title">Clients</h4>
          <div className="bm-client-stats">
            <div>
              <span className="bm-tile-label">Returning clients (est.)</span>
              <span className="bm-client-value">{m.clients.repeatRatePct ?? "--"}%</span>
            </div>
            <div>
              <span className="bm-tile-label">New</span>
              <span className="bm-client-value">{m.clients.newCount}</span>
            </div>
            <div>
              <span className="bm-tile-label">Returning</span>
              <span className="bm-client-value">{m.clients.repeatCount}</span>
            </div>
            <div>
              <span className="bm-tile-label">Top 10 share</span>
              <span className="bm-client-value">{m.clients.concentrationTop10Pct ?? "--"}%</span>
            </div>
          </div>
          <table className="bm-table">
            <tbody>
              {(m.clients.topClients || []).map((t, i) => (
                <tr key={`${t.name}-${i}`}>
                  <td className="bm-rank-label">{t.name}</td>
                  <td className="bm-rank-jobs">{t.jobs}</td>
                  <td className="bm-rank-value">{priceStrFormatter(t.revenueCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bm-panel-note">
            Clients are matched by name, so this is an estimate with a known downward bias — name
            variations and typos split one client into several. Top clients are lifetime totals.
          </div>
        </div>
      )}

      {/* ── Year detail ── */}
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
        <h4 className="bm-panel-title">Year Detail</h4>
        <div className="bm-table-scroll">
          <table className="bm-table bm-table-wide">
            <thead>
              <tr>
                <th>Year</th>
                <th className="bm-num">Revenue</th>
                <th className="bm-num">Jobs</th>
                <th className="bm-num">Avg Job</th>
                <th className="bm-num">New clients</th>
                <th className="bm-num">Returning</th>
                <th className="bm-num">vs prior year</th>
              </tr>
            </thead>
            <tbody>
              {[...(m.byYear || [])].reverse().map((y, idx, arr) => {
                // arr is newest-first, so the prior year is the NEXT element.
                const prior = arr[idx + 1];
                // 2013 anchors no growth figure (partial first year).
                const comparable =
                  prior && prior.year >= (m.meta?.firstFullYear ?? 2014) && y.year > (m.meta?.firstFullYear ?? 2014);
                return (
                  <tr key={y.year}>
                    <td>{y.year}</td>
                    <td className="bm-num bm-money">{priceStrFormatter(y.revenueCents)}</td>
                    <td className="bm-num">{y.jobs}</td>
                    <td className="bm-num">{priceStrFormatter(y.avgJobValueCents)}</td>
                    <td className="bm-num">{y.newClients}</td>
                    <td className="bm-num bm-muted">{y.repeatClients}</td>
                    <td className="bm-num">
                      {comparable ? (
                        <DeltaBadge value={deltaPct(y.revenueCents, prior.revenueCents)} />
                      ) : (
                        <span className="bm-delta bm-delta-none">n/a</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="bm-panel-note">
          {m.meta?.firstFullYear ? m.meta.firstFullYear - 1 : 2013} shows no growth figure: it was a
          partial first year of record-keeping, so a comparison against it would report growth that
          is an artefact of when the records start. Client counts are name-matched estimates.
        </div>
      </div>

      {/* ── Month detail ── */}
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-depth">
        <h4 className="bm-panel-title">Month Detail — {m.meta?.displayYear}</h4>
        <div className="bm-table-scroll">
          <table className="bm-table bm-table-wide">
            <thead>
              <tr>
                <th>Month</th>
                <th className="bm-num">Revenue</th>
                <th className="bm-num">Jobs</th>
                <th className="bm-num">Avg Job</th>
                <th className="bm-num">% of {allYears ? "year" : "period"}</th>
                <th className="bm-num">vs last year</th>
              </tr>
            </thead>
            <tbody>
              {(m.byMonth || []).map((mo) => (
                <tr key={mo.month} className={mo.partial ? "bm-row-partial" : ""}>
                  <td>
                    {MONTHS[mo.month - 1]}
                    {mo.partial && <span className="bm-partial-tag">partial</span>}
                  </td>
                  <td className="bm-num bm-money">{priceStrFormatter(mo.revenueCents)}</td>
                  <td className="bm-num">{mo.jobs}</td>
                  <td className="bm-num">{priceStrFormatter(mo.avgJobValueCents)}</td>
                  <td className="bm-num bm-muted">
                    {mo.pctOfPeriod === null || mo.pctOfPeriod === undefined ? "--" : `${mo.pctOfPeriod}%`}
                  </td>
                  <td className="bm-num">
                    {mo.priorYearRevenueCents === null ? (
                      <span className="bm-delta bm-delta-none">n/a</span>
                    ) : (
                      <DeltaBadge value={deltaPct(mo.revenueCents, mo.priorYearRevenueCents)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SoftwareExpensesPanel />

      <div className="bm-footer-note">
        Revenue and job counts only — there is no cost or expense data in the ledger, so nothing here
        is profit or margin.
        {m.meta?.unparseableCount > 0 && (
          <strong className="bm-warn"> {m.meta.unparseableCount} ledger rows could not be read and are excluded.</strong>
        )}
      </div>
    </div>
  );
};

export default BusinessMetricsSection;
